import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import {
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ColumnSearch from "../../../components/ColumnSearch";
import useDialogStore from "../../../stores/useDialogStore";
import ClientForm from "../Client/ClientForm";
import EditableCell from "../../../components/EditableCell";
import { toast } from "react-toastify";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";

export default function ClientsList() {
  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialogStore();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });

  const { data: clients, isLoading } = useQuery({
    queryKey: ["clients", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      // Ensure sort parameters are properly formatted
      const sortBy = params.get("sortBy");
      const order = params.get("order");

      if (sortBy) {
        params.set("sortBy", sortBy);
        params.set("order", order || "asc");
      }

      const response = await axiosInstance.get(
        `${
          window.REACT_APP_API_ENDPOINT
        }/trash/deleted-clients?${params.toString()}`
      );
      return response.data;
    },
    keepPreviousData: true,
  });

  // Restore client mutation
  const restoreClientMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.post(`${window.REACT_APP_API_ENDPOINT}/trash/restore`, {
        id,
        type: "clients",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["clients"]);
      closeDialog();
      toast.success("Client restored successfully!");
    },
    onError: (error) => {
      toast.error("Failed to restore client. Please try again.");
      console.error("Restore client error:", error);
    },
  });

  // Delete client mutation
  const deleteClientMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/trash/delete-permanently`,
        {
          id,
          type: "clients",
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["clients"]);
      closeDialog();
      toast.success("Client deleted permanently!");
    },
    onError: (error) => {
      toast.error("Failed to delete client. Please try again.");
      console.error("Delete client error:", error);
    },
  });

  const handleRestore = (id, name) => {
    openDialog({
      title: "Restore Client",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to restore{" "}
            <span className="font-bold">{name}</span>?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={() => {
                restoreClientMutation.mutate(id);
              }}
            >
              Restore
            </Button>
          </div>
        </div>
      ),
    });
  };

  const handleDelete = (id, name) => {
    openDialog({
      title: "Delete Client Permanently",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to permanently delete{" "}
            <span className="font-bold text-red-600 ">{name}</span>?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                deleteClientMutation.mutate(id);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
    // Force refetch when filters change
    queryClient.invalidateQueries(["clients"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    // Ensure we're using the correct parameter names for the API
    const sortBy = params.get("sortBy");
    const order = params.get("order");

    // Update the URL with the correct parameters
    const newParams = new URLSearchParams();
    if (sortBy) newParams.set("sortBy", sortBy);
    if (order) newParams.set("order", order);

    // Preserve other existing parameters
    for (const [key, value] of params.entries()) {
      if (key !== "sortBy" && key !== "order") {
        newParams.set(key, value);
      }
    }

    navigate(`${location.pathname}?${newParams.toString()}`);
    queryClient.invalidateQueries(["clients"]);
  };

  const handleClearFilters = () => {
    // Reset sort config
    setSortConfig({
      key: null,
      direction: "desc",
    });

    // Clear URL parameters and navigate to base URL
    navigate(location.pathname);

    // Force refetch the data
    queryClient.invalidateQueries(["clients"]);
  };

  const editCellMutation = useMutation({
    mutationFn: ({ clientId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/clients/${clientId}`,
        { [field]: value }
      ),

    onSuccess: () => {
      queryClient.invalidateQueries(["clients"]);
      toast.success("Client updated successfully");
    },
    onError: (error) => {
      console.error("Edit cell error:", error);
      toast.error("Failed to update client");
    },
  });

  const handleOpenClientForm = (client = null) => {
    openDialog({
      title: client ? "Edit Client" : "Add New Client",
      content: (
        <ClientForm
          payload={client}
          onSuccess={() => {
            closeDialog();
            queryClient.invalidateQueries(["clients"]);
          }}
        />
      ),
    });
  };

  // Selection state
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Selection handlers
  const clientsList = clients?.data?.data || [];
  const handleSelectClient = (clientId) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
    setSelectAll(newSelected.size === clientsList.length);
  };
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedClients(new Set());
      setSelectAll(false);
    } else {
      setSelectedClients(new Set(clientsList.map((client) => client.id)));
      setSelectAll(true);
    }
  };
  const handleClearSelection = () => {
    setSelectedClients(new Set());
    setSelectAll(false);
  };
  // Bulk actions
  const handleBulkRestore = () => {
    if (selectedClients.size === 0) {
      toast.warning("Please select at least one client to restore.");
      return;
    }
    openDialog({
      title: "Restore Multiple Clients",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to restore {selectedClients.size} client
            {selectedClients.size > 1 ? "s" : ""}?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              icon={ArrowPathIcon}
              onClick={() => {
                axiosInstance
                  .post(`${window.REACT_APP_API_ENDPOINT}/trash/restore`, {
                    ids: Array.from(selectedClients),
                    type: "clients",
                  })
                  .then(() => {
                    queryClient.invalidateQueries(["clients"]);
                    setSelectedClients(new Set());
                    setSelectAll(false);
                    closeDialog();
                    toast.success("Clients restored successfully!");
                  })
                  .catch(() => {
                    toast.error(
                      "Failed to restore some clients. Please try again."
                    );
                  });
              }}
            >
              Restore All
            </Button>
          </div>
        </div>
      ),
    });
  };
  const handleBulkDelete = () => {
    if (selectedClients.size === 0) {
      toast.warning("Please select at least one client to delete permanently.");
      return;
    }
    openDialog({
      title: "Delete Permanently",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg text-red-600 font-semibold">
            ⚠️ WARNING: This action cannot be undone!
          </p>
          <p className="text-lg">
            Are you sure you want to permanently delete {selectedClients.size}{" "}
            client{selectedClients.size > 1 ? "s" : ""}? This action will
            completely remove the client{selectedClients.size > 1 ? "s" : ""}{" "}
            from the database.
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={TrashIcon}
              onClick={() => {
                axiosInstance
                  .post(
                    `${window.REACT_APP_API_ENDPOINT}/trash/delete-permanently`,
                    {
                      ids: Array.from(selectedClients),
                      type: "clients",
                    }
                  )
                  .then(() => {
                    queryClient.invalidateQueries(["clients"]);
                    setSelectedClients(new Set());
                    setSelectAll(false);
                    closeDialog();
                    toast.success("Clients deleted permanently!");
                  })
                  .catch(() => {
                    toast.error(
                      "Failed to delete some clients permanently. Please try again."
                    );
                  });
              }}
            >
              Delete Permanently
            </Button>
          </div>
        </div>
      ),
    });
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
            <Button
              variant="outline-info"
              icon={<ArrowLeftIcon className="h-4 w-4" />}
              onClick={() => navigate(`/admin/trash`)}
              className="p-1"
            >
              Back to Trash Categories
            </Button>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <div className="flex flex-col gap-2">
                  <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                    Deleted Clients
                  </h4>
                  {selectedClients.size > 0 && (
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>
                        {selectedClients.size} client
                        {selectedClients.size > 1 ? "s" : ""} selected
                      </span>
                      <Button
                        variant="light"
                        size="xs"
                        onClick={handleClearSelection}
                      >
                        Clear Selection
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 lg:flex-row items-center">
                  {selectedClients.size > 0 && (
                    <>
                      <Button
                        variant="outline-success"
                        size="sm"
                        icon={ArrowPathIcon}
                        onClick={handleBulkRestore}
                      >
                        Restore Selected ({selectedClients.size})
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        icon={TrashIcon}
                        onClick={handleBulkDelete}
                        className=""
                      >
                        Delete Permanently ({selectedClients.size})
                      </Button>
                    </>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                    onClick={handleClearFilters}
                    className="mb-2 lg:mb-0"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Full Name"
                            columnKey="name"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by name..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Unique ID"
                            columnKey="unique_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Unique ID..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : clientsList.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <NoData message="No clients found" />
                        </td>
                      </tr>
                    ) : (
                      clientsList.map((client, index) => (
                        <tr
                          key={client.id}
                          className={`divide-y divide-gray-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }`}
                        >
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <input
                              type="checkbox"
                              checked={selectedClients.has(client.id)}
                              onChange={() => handleSelectClient(client.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={client.name || "—"}
                                displayValue={client.name || "—"}
                                id={client.id}
                                field="name"
                                maxLength={50}
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    clientId: data.id,
                                    field: "name",
                                    value: data.value,
                                  })
                                }
                              />
                            </div>
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={client.unique_id || "—"}
                                displayValue={client.unique_id || "—"}
                                id={client.id}
                                field="unique_id"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    clientId: data.id,
                                    field: "unique_id",
                                    value: data.value,
                                  })
                                }
                              />
                            </div>
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4 gap-2">
                              <Button
                                variant="outline-success"
                                size="icon"
                                icon={<ArrowPathIcon className="h-4 w-4" />}
                                onClick={() =>
                                  handleRestore(client.id, client.name)
                                }
                                className="p-1"
                              />
                              <Button
                                variant="outline-danger"
                                size="icon"
                                icon={<TrashIcon className="h-4 w-4" />}
                                onClick={() =>
                                  handleDelete(client.id, client.name)
                                }
                                className="p-1"
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={clients?.data?.current_page}
                last_page={clients?.data?.last_page}
                next_page_url={clients?.data?.next_page_url}
                prev_page_url={clients?.data?.prev_page_url}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
